/**
 * Helpers for pre-filling the Intercom signup page from a marketing-site trial CTA.
 *
 * The signup app reads these two query params to pre-fill the form, holding each
 * to the same validator a typed-in value gets:
 *
 *   - `email`        — any strictly-valid email address. Pre-fills in all flows.
 *   - `company_size` — MUST be one of SIGNUP_COMPANY_SIZES; any other value is
 *                      dropped rather than seeded.
 */

/** Query param names read by the signup page for pre-fill. */
export const SIGNUP_PREFILL_PARAMS = {
  email: "email",
  companySize: "company_size",
} as const;

import type { SignupCompanySize } from "./signup-company-sizes";

export {
  SIGNUP_COMPANY_SIZES,
  type SignupCompanySize,
} from "./signup-company-sizes";

/**
 * Maps the contact-sales form's `numEmployeesIntercom` option value to the
 * signup page's accepted `company_size` string. The marketing form's 6 buckets
 * don't line up 1:1 with the signup's 5, so the two largest form buckets collapse
 * into "2,000+". Returns null for unknown/empty values so the CTA omits the param
 * rather than sending one the controller rejects.
 *
 * Form option values live in apps/<app>/.../contact-sales form-fields.json.
 */
export function mapFormCompanySizeToSignup(
  formValue: string | null | undefined,
): SignupCompanySize | null {
  switch (formValue) {
    case "5":
    case "15":
      return "1-15";
    case "49":
      return "16-49";
    case "199":
      return "50-199";
    case "1999":
      return "200-1,999";
    case "4999":
    case "5000":
      return "2,000+";
    default:
      return null;
  }
}

export type TrialPrefillValues = {
  email?: string | null;
  companySize?: string | null;
};

/**
 * Returns `baseUrl` with the signup pre-fill params appended. Only sets params
 * that have a truthy value. Falls back to the unmodified `baseUrl` if it can't
 * be parsed as a URL, so a bad input can never break the CTA.
 */
export function buildTrialSignupUrl(
  baseUrl: string,
  { email, companySize }: TrialPrefillValues,
): string {
  try {
    const isRootRelative = baseUrl.startsWith("/") && !baseUrl.startsWith("//");
    const url = new URL(
      baseUrl,
      isRootRelative ? "https://relative.invalid" : undefined,
    );
    if (email) url.searchParams.set(SIGNUP_PREFILL_PARAMS.email, email);
    if (companySize) {
      url.searchParams.set(SIGNUP_PREFILL_PARAMS.companySize, companySize);
    }
    return isRootRelative
      ? `${url.pathname}${url.search}${url.hash}`
      : url.toString();
  } catch {
    return baseUrl;
  }
}
